import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { getBranchDetailsUrl } from 'configs/routes';
import { LocalTimeDate, useAuth } from 'hooks';
import { EOrderBookingTypes, EOrderStatus } from 'types/api';
import { ICardHeadTitleProps } from './types';
import CustomDivider from 'components/CustomDivider';
import AssignedDriverTag from 'components/_tags/AssignedDriverTag';
import DeliveryModeTag from 'components/_tags/DeliveryModeTag';
import OrderBookingTypeTag from 'components/_tags/OrderBookingTypeTag';
import OrderStatusTag from 'components/_tags/OrderStatusTag';
import { Flex, Space } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';

export const CardHeadTitle: FC<ICardHeadTitleProps> = memo(({ card }) => {
	const auth = useAuth();
	const { t: tDispatcher } = useTranslation('dispatcher');

	const {
		vendorId,
		branch,
		status: orderStatus,
		status_description: orderStatusDescription,
		driver_name: driverName,
		pickup_time: pickupTime,
		booking_type: bookingType,
		delivery_mode: deliveryMode,
		delivery_mode_description: deliveryModeDescription,
		delivery_mode_description_detailed: deliveryModeDescriptionDetailed,
	} = card;

	const isScheduledOrder = bookingType === EOrderBookingTypes.SCHEDULED;

	// ! render
	const scheduledPickupTimeLabel =
		pickupTime?.length && isScheduledOrder ? (
			<Flex gap='small'>
				<CalendarOutlined />
				<LocalTimeDate date={pickupTime} />
			</Flex>
		) : null;

	return (
		<Space split={<CustomDivider type='vertical' />}>
			<NavLink to={getBranchDetailsUrl(auth.user, vendorId, branch.id)}>{branch.name}</NavLink>

			<OrderStatusTag
				status={orderStatus}
				description={
					<Flex vertical>
						{orderStatusDescription}
						{scheduledPickupTimeLabel}
					</Flex>
				}
			/>

			<DeliveryModeTag
				deliveryMode={deliveryMode}
				deliveryModeDescription={deliveryModeDescription}
				deliveryModeDescriptionDetailed={deliveryModeDescriptionDetailed}
			/>

			{/* Display for all tabs except "SCHEDULED" tab */}
			{isScheduledOrder && orderStatus !== EOrderStatus.SCHEDULED ? (
				<OrderBookingTypeTag
					type={bookingType}
					tooltipText={scheduledPickupTimeLabel}
				>
					{tDispatcher('card_head_title.scheduled_order')}
				</OrderBookingTypeTag>
			) : null}

			{driverName && <AssignedDriverTag driverName={driverName} />}
		</Space>
	);
});
